import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { calculatePlayerPerformanceScore } from '../utils/playerUtils';
import { getTeamLogo, normalizeTeamName } from '../utils/teamUtils';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

const VenueStats = ({ players, isDarkMode }) => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [selectedStat, setSelectedStat] = useState('all_run_metres');
  const [selectedVenue, setSelectedVenue] = useState('');
  const [venueStats, setVenueStats] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYears, setSelectedYears] = useState(['2025']);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [showGroupedPositions, setShowGroupedPositions] = useState(true);
  const [expandedCell, setExpandedCell] = useState(null); // {venue, position}
  const [rawGamesData, setRawGamesData] = useState({});
  
  // Stats by category for dropdown
  const statCategories = [
    { label: "Defense", options: [
      { value: 'tackles_made', label: 'Tackles Made' },
      { value: 'missed_tackles', label: 'Missed Tackles' },
      { value: 'ineffective_tackles', label: 'Ineffective Tackles' },
      { value: 'one_on_one_steal', label: 'One on One Steal' }
    ]},
    { label: "Attack", options: [
      { value: 'all_run_metres', label: 'Run Metres' },
      { value: 'post_contact_metres', label: 'Post Contact Metres' },
      { value: 'line_breaks', label: 'Line Breaks' },
      { value: 'tackle_breaks', label: 'Tackle Breaks' },
      { value: 'offloads', label: 'Offloads' },
      { value: 'try_assists', label: 'Try Assists' }
    ]},
    { label: "Kicking", options: [
      { value: 'kicks', label: 'Kicks' },
      { value: 'kick_metres', label: 'Kick Metres' },
      { value: 'forced_drop_outs', label: 'Forced Drop Outs' },
      { value: 'bomb_kicks', label: 'Bomb Kicks' },
      { value: 'grubbers', label: 'Grubbers' },
      { value: 'cross_field_kicks', label: 'Cross Field Kicks' }
    ]},
    { label: "Scoring", options: [
      { value: 'tries', label: 'Tries' },
      { value: 'points', label: 'Points' },
      { value: 'errors', label: 'Errors' },
      { value: 'penalties', label: 'Penalties' }
    ]}
  ];

  // Get available years
  const years = ['2025', '2024', '2023', '2022', '2021', '2020'];
  
  // Get unique venues from data
  const venues = useMemo(() => {
    if (!players || players.length === 0) return [];
    
    const venueSet = new Set();
    players.forEach(player => {
      if (player.venue) {
        venueSet.add(player.venue);
      }
    });
    
    return Array.from(venueSet).sort();
  }, [players]);
  
  // Statistic options (similar to team dashboard)
  const statOptions = {
    'all_run_metres': { label: 'Run Metres', thresholds: { low: 80, medium: 120, high: 160 } },
    'tackles_made': { label: 'Tackles', thresholds: { low: 10, medium: 20, high: 30 } },
    'tries': { label: 'Tries', thresholds: { low: 0.3, medium: 0.6, high: 1 } },
    'line_breaks': { label: 'Line Breaks', thresholds: { low: 0.3, medium: 0.7, high: 1.2 } },
    'tackle_breaks': { label: 'Tackle Breaks', thresholds: { low: 1, medium: 3, high: 5 } },
    'kick_metres': { label: 'Kick Metres', thresholds: { low: 100, medium: 300, high: 450 } },
    'offloads': { label: 'Offloads', thresholds: { low: 0.5, medium: 1.2, high: 2 } },
    'try_assists': { label: 'Try Assists', thresholds: { low: 0.2, medium: 0.5, high: 0.8 } },
    'kicks': { label: 'Kicks', thresholds: { low: 2, medium: 5, high: 10 } },
    'missed_tackles': { label: 'Missed Tackles', thresholds: { low: 1, medium: 2, high: 4 }, inverse: true },
    'ineffective_tackles': { label: 'Ineffective Tackles', thresholds: { low: 1, medium: 2, high: 3 }, inverse: true },
    'errors': { label: 'Errors', thresholds: { low: 0.5, medium: 1, high: 2 }, inverse: true },
    'penalties': { label: 'Penalties', thresholds: { low: 0.5, medium: 1, high: 2 }, inverse: true },
    'one_on_one_steal': { label: 'One on One Steal', thresholds: { low: 0.1, medium: 0.3, high: 0.5 } },
    'post_contact_metres': { label: 'Post Contact Metres', thresholds: { low: 30, medium: 50, high: 70 } },
    'forced_drop_outs': { label: 'Forced Drop Outs', thresholds: { low: 0.1, medium: 0.3, high: 0.5 } },
    'bomb_kicks': { label: 'Bomb Kicks', thresholds: { low: 0.5, medium: 1.5, high: 3 } },
    'grubbers': { label: 'Grubbers', thresholds: { low: 0.3, medium: 0.8, high: 1.5 } },
    'cross_field_kicks': { label: 'Cross Field Kicks', thresholds: { low: 0.2, medium: 0.5, high: 1 } },
    'points': { label: 'Points', thresholds: { low: 2, medium: 6, high: 12 } },
  };
  
  // Update position groups to match NRL positions
  const positionGroups = {
    'Spine': ['Fullback', 'Halfback', 'Five-Eighth', 'Hooker'],
    'Backs': ['Centre', 'Winger'],
    'Forwards': ['Prop', '2nd Row', 'Lock'],
    'Bench': ['Interchange', 'Replacement']
  };

  // Update column headers to use NRL positions
  const venueColumns = [
    { id: 'Spine', label: 'Spine' },
    { id: 'Backs', label: 'Backs' },
    { id: 'Forwards', label: 'Forwards' },
    { id: 'Bench', label: 'Bench' }
  ];
  
  // Calculate stats when selected stat, venue, or year changes
  useEffect(() => {
    if (!players || players.length === 0) return;
    
    setLoading(true);
    
    try {
      // Filter by selected years
      const filteredPlayers = players.filter(p => selectedYears.includes(String(p.year)));
      
      // Calculate venue stats
      const venueData = {};
      const rawData = {};
      
      // Process each venue
      venues.forEach(venue => {
        if (!venue) return;
        
        venueData[venue] = {};
        rawData[venue] = {};
        
        // Calculate stats for each position group AND individual position
        Object.keys(positionGroups).forEach(group => {
          const positions = positionGroups[group];
          
          // Store raw games data for position group
          const groupGames = filteredPlayers.filter(
            p => p.venue === venue && positions.includes(p.position)
          );
          rawData[venue][group] = groupGames;
          
          if (groupGames.length > 0) {
            const total = groupGames.reduce((sum, game) => {
              const value = parseFloat(game[selectedStat]) || 0;
              return sum + value;
            }, 0);
            
            venueData[venue][group] = parseFloat((total / groupGames.length).toFixed(1));
          } else {
            venueData[venue][group] = '-';
          }
          
          // Calculate and store individual position stats
          positions.forEach(position => {
            const positionGames = filteredPlayers.filter(
              p => p.venue === venue && p.position === position
            );
            rawData[venue][position] = positionGames; // Store individual position games
            
            if (positionGames.length > 0) {
              const total = positionGames.reduce((sum, game) => {
                const value = parseFloat(game[selectedStat]) || 0;
                return sum + value;
              }, 0);
              
              venueData[venue][position] = parseFloat((total / positionGames.length).toFixed(1));
            } else {
              venueData[venue][position] = '-';
            }
          });
        });
        
        // Add game count to venue data
        venueData[venue].gameCount = filteredPlayers.filter(p => p.venue === venue)
          .reduce((unique, p) => {
            const key = `${p.round}_${p.player_team}_${p.home_team}_${p.away_team}`;
            unique.add(key);
            return unique;
          }, new Set()).size;
      });
      
      // Convert venue data to array format
      const venueStatsArray = Object.entries(venueData).map(([venue, stats]) => ({
        venue,
        gameCount: stats.gameCount || 0,
        ...stats
      }));
      
      setVenueStats(venueStatsArray);
      setRawGamesData(rawData);
      
      // If a venue is selected, calculate player stats for that venue
      if (selectedVenue) {
        const venuePlayerGames = filteredPlayers.filter(p => p.venue === selectedVenue);
        
        // Group games by player
        const playerGamesMap = {};
        venuePlayerGames.forEach(game => {
          if (!playerGamesMap[game.player_name]) {
            playerGamesMap[game.player_name] = [];
          }
          playerGamesMap[game.player_name].push(game);
        });
        
        // Calculate stats for each player
        const playerStatsArray = Object.entries(playerGamesMap).map(([playerName, games]) => {
          // Skip if no games
          if (games.length === 0) return null;
          
          // Get most recent game for position, team
          const latestGame = games[0];
          
          // Calculate stats
          const statValue = games.reduce((sum, game) => {
            return sum + (parseFloat(game[selectedStat]) || 0);
          }, 0) / games.length;
          
          // Get all stats for display
          const allStats = {};
          Object.keys(latestGame).forEach(key => {
            if (statOptions[key]) {
              allStats[key] = games.reduce((sum, game) => {
                return sum + (parseFloat(game[key]) || 0);
              }, 0) / games.length;
            }
          });
          
          // Calculate performance points
          const pp = calculatePlayerPerformanceScore(latestGame);
          
          return {
            id: playerName,
            player: playerName,
            team: latestGame.player_team,
            position: latestGame.position,
            round: latestGame.round,
            [selectedStat]: parseFloat(statValue.toFixed(1)),
            games: games.length,
            ...allStats,
            performance_points: pp.toFixed(1)
          };
        }).filter(Boolean);
        
        // Sort by selected stat, descending
        playerStatsArray.sort((a, b) => b[selectedStat] - a[selectedStat]);
        
        setPlayerStats(playerStatsArray);
      } else {
        setPlayerStats([]);
      }
    } catch (error) {
      console.error('Error processing venue stats:', error);
    } finally {
      setLoading(false);
    }
  }, [players, selectedStat, selectedVenue, selectedYears, venues]);
  
  // Updated color class getter to match the bubble design from the image
  const getColorClass = (value, columnId, allValues) => {
    if (value === '-' || isNaN(value)) return 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    
    // Get all numeric values for this column
    const numericValues = allValues
      .map(v => parseFloat(v[columnId]))
      .filter(v => !isNaN(v));
    
    if (numericValues.length === 0) return '';
    
    // Sort values to determine percentiles
    const sortedValues = [...numericValues].sort((a, b) => a - b);
    const index = sortedValues.indexOf(parseFloat(value));
    const percentile = index / sortedValues.length;
    
    // Color based on percentile
    if (percentile >= 0.75) return 'bg-green-500 text-white';
    if (percentile >= 0.5) return 'bg-lime-400 text-black';
    if (percentile >= 0.25) return 'bg-yellow-400 text-black';
    return 'bg-red-400 text-white';
  };
  
  // Calculate paginated player data
  const paginatedPlayerData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return playerStats.slice(startIndex, startIndex + pageSize);
  }, [playerStats, currentPage, pageSize]);
  
  // Calculate total pages
  const totalPages = Math.ceil(playerStats.length / pageSize);
  
  // Format stat name for display
  const formatStatLabel = (statId) => {
    return statOptions[statId]?.label || statId;
  };

  // Add sort handler
  const handleSort = (columnId) => {
    setSortConfig(prevConfig => {
      // If sorting by position group or individual position
      const newDirection = prevConfig.key === columnId && prevConfig.direction === 'asc' ? 'desc' : 'asc';
      return {
        key: columnId,
        direction: newDirection
      };
    });
  };

  // Sort and filter venue stats
  const sortedVenueStats = useMemo(() => {
    let filteredStats = venueStats.filter(venue => {
      // Filter based on whether we're showing grouped or individual positions
      const columnsToCheck = showGroupedPositions 
        ? venueColumns.map(col => col.id)
        : Object.values(positionGroups).flat();
        
      const hasData = columnsToCheck.some(col => 
        venue[col] !== '-' && venue[col] !== null && venue[col] !== undefined
      );
      return hasData;
    });

    if (!sortConfig.key) return filteredStats;

    return [...filteredStats].sort((a, b) => {
      const aValue = parseFloat(a[sortConfig.key]) || 0;
      const bValue = parseFloat(b[sortConfig.key]) || 0;
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [venueStats, sortConfig, showGroupedPositions]);

  const getPositionData = (venue) => {
    if (showGroupedPositions) {
      return venueColumns.map(column => ({
        id: column.id,
        label: column.label,
        value: venue[column.id],
        games: rawGamesData[venue.venue]?.[column.id] || []
      }));
    } else {
      return Object.entries(positionGroups).flatMap(([group, positions]) => 
        positions.map(position => ({
          id: position,
          label: position,
          value: venue[position] || '-',
          games: rawGamesData[venue.venue]?.[position] || []
        }))
      );
    }
  };

  // Add handler for cell click
  const handleCellClick = (venue, position) => {
    console.log('Clicked:', venue, position); // Add this for debugging
    console.log('Raw data:', rawGamesData[venue]?.[position]); // Add this for debugging
    
    if (expandedCell && expandedCell.venue === venue && expandedCell.position === position) {
      setExpandedCell(null);
    } else {
      const games = rawGamesData[venue]?.[position] || [];
      if (games.length > 0) {
        setExpandedCell({ venue, position });
      }
    }
  };

  // Add helper to check if cell is expanded
  const isCellExpanded = (venue, position) => {
    return expandedCell && 
           expandedCell.venue === venue && 
           expandedCell.position === position;
  };

  return (
    <div className="h-full w-full">
      <div className="mb-2 md:mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
          <div>
            <h2 className="text-lg md:text-xl font-bold dark:text-white">Venue Statistics</h2>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300">
              How players perform at different venues. Data shows average statistics by position group at each venue.
            </p>
          </div>
          
          <div className="hidden md:block w-60">
            <select
              value={selectedStat}
              onChange={(e) => setSelectedStat(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {statCategories.flatMap(category => [
                <optgroup key={category.label} label={category.label}>
                  {category.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ])}
            </select>
          </div>
        </div>
        
        <div className="flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-4">
          <div className="flex flex-wrap items-center gap-1 md:gap-2">
            {years.map(y => (
              <button
                key={y}
                className={`flex-shrink-0 px-1.5 py-0.5 md:px-2.5 md:py-1 text-[10px] md:text-xs rounded-md transition-colors duration-150 ${
                  selectedYears.includes(y)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                onClick={() => {
                  setSelectedYears(prev => 
                    prev.includes(y) 
                      ? prev.filter(year => year !== y)
                      : [...prev, y]
                  );
                }}
              >
                {y}
              </button>
            ))}
          </div>
          
          <div className="hidden md:block ml-4">
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="w-40 px-2 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Venues</option>
              {venues.map(venue => (
                <option key={venue} value={venue}>{venue}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="groupPositions"
              checked={showGroupedPositions}
              onChange={() => setShowGroupedPositions(prev => !prev)}
              className="h-3 w-3 md:h-4 md:w-4 text-blue-600 rounded"
            />
            <label htmlFor="groupPositions" className="ml-1 text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Grouped Positions
            </label>
          </div>
          
          <div className="md:hidden flex-grow">
            <select
              value={selectedStat}
              onChange={(e) => setSelectedStat(e.target.value)}
              className="w-full px-1.5 py-0.5 text-[10px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {statCategories.flatMap(category => [
                <optgroup key={category.label} label={category.label}>
                  {category.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ])}
            </select>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full md:w-full divide-y divide-gray-200 dark:divide-gray-700 text-[10px] md:text-sm table-fixed">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-20">
                <tr>
                  <th 
                    className="px-1.5 py-1.5 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 w-[100px] md:w-[180px] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                    onClick={() => handleSort('venue')}
                  >
                    <div className="flex items-center justify-between">
                      <span>VENUE</span>
                      {sortConfig.key === 'venue' && (
                        <span className="ml-0.5">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  {getPositionData(sortedVenueStats[0]).map(position => (
                    <th 
                      key={position.id} 
                      className="px-1 py-1 md:px-3 md:py-2 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150 md:w-24"
                      onClick={() => handleSort(position.id)}
                    >
                      <div className="flex flex-col items-center">
                        <span className="whitespace-nowrap">{position.label}</span>
                        {sortConfig.key === position.id && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedVenueStats.map((venue, index) => {
                  const hasExpandedCell = getPositionData(venue).some(({ id }) => 
                    isCellExpanded(venue.venue, id)
                  );
                  
                  return (
                    <React.Fragment key={venue.venue}>
                      <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${
                        hasExpandedCell ? 'bg-gray-50 dark:bg-gray-750' : ''
                      }`}>
                        <td className="px-1.5 py-1.5 md:px-3 md:py-2 text-[10px] md:text-sm font-medium text-gray-800 dark:text-gray-200 sticky left-0 bg-white dark:bg-gray-800 z-10 w-[100px] md:w-[180px]">
                          <div className="flex items-start">
                            <span className="truncate max-w-[95px] md:max-w-full">{venue.venue} <span className="text-gray-500 dark:text-gray-400">({venue.gameCount})</span></span>
                          </div>
                        </td>
                        
                        {getPositionData(venue).map(({ id, value, games }) => {
                          const isActive = isCellExpanded(venue.venue, id);
                          const hasData = games && games.length > 0;
                          
                          return (
                            <td 
                              key={id}
                              onClick={() => hasData && handleCellClick(venue.venue, id)}
                              className="px-1 py-1 md:px-2 md:py-2 text-center text-[10px] md:text-sm cursor-pointer transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <div className={`rounded-full inline-block py-0.5 px-1.5 md:py-1.5 md:px-3 min-w-[32px] md:min-w-[48px] font-medium
                                ${getColorClass(value, id, sortedVenueStats)} ${isActive ? 'ring-2 ring-blue-500' : ''} 
                                ${hasData ? 'hover:opacity-90 hover:ring-1 hover:ring-gray-400 dark:hover:ring-gray-500 transition-all duration-150' : ''}`}
                              >
                                {value}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      
                      {hasExpandedCell && (
                        <tr className="bg-gray-50 dark:bg-gray-750">
                          <td colSpan={getPositionData(venue).length + 1} className="p-0">
                            {getPositionData(venue).map(({ id, games }) => {
                              if (!isCellExpanded(venue.venue, id)) return null;
                              if (!games || games.length === 0) return null;

                              // Sort games chronologically descending
                              const sortedGames = [...games].sort((a, b) => {
                                const aRound = parseInt(a.round.replace('Round ', ''));
                                const bRound = parseInt(b.round.replace('Round ', ''));
                                
                                if (a.year !== b.year) {
                                  return b.year - a.year;
                                }
                                return bRound - aRound;
                              });

                              return (
                                <div key={id} className="p-2 md:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                                  <div className="flex justify-between items-center mb-2 md:mb-3">
                                    <h3 className="text-[10px] md:text-sm font-medium text-gray-900 dark:text-white flex items-center">
                                      <span className="inline-block w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 mr-1 md:mr-2"></span>
                                      <span className="font-semibold">{venue.venue} - {id}</span> Player Stats ({formatStatLabel(selectedStat)})
                                    </h3>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedCell(null);
                                      }}
                                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-150 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                    >
                                      <svg className="h-3 w-3 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                  
                                  <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-[10px] md:text-sm">
                                      <thead className="bg-gray-50 dark:bg-gray-700">
                                        <tr>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Player
                                          </th>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Team
                                          </th>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Round
                                          </th>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                            {formatStatLabel(selectedStat)}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {sortedGames.map((game, index) => (
                                          <tr key={index} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150">
                                            <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm font-medium text-gray-800 dark:text-gray-200">
                                              {game.player_name}
                                            </td>
                                            <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-gray-600 dark:text-gray-300">
                                              <div className="flex items-center">
                                                <img 
                                                  src={getTeamLogo(game.player_team)} 
                                                  alt={game.player_team} 
                                                  className="hidden md:inline-block h-4 w-4 mr-1"
                                                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/16?text=NRL'; }}
                                                />
                                                {game.player_team}
                                              </div>
                                            </td>
                                            <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-gray-600 dark:text-gray-300">
                                              {game.round}
                                            </td>
                                            <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-center">
                                              <div className={`rounded-full inline-block py-0.5 px-1.5 md:py-1 md:px-2 min-w-[32px] md:min-w-[40px] font-medium ${
                                                getColorClass(game[selectedStat], selectedStat, sortedVenueStats)
                                              }`}>
                                                {game[selectedStat]}
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueStats; 